import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ZonalGrossMarginFilter } from "@/components/common/section-components/filters/ZonalGrossMarginFilter";
import { ZonalGrossMarginActions } from "@/components/common/section-components/action/ZonalGrossMarginActions";
import { NoData } from "@/components/common/section-components/NoData";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { GetZonalGrossMarginListProps } from "@/services/zonal-gross-margin.service";
import { isEmpty } from "@/utils/query-string";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  clearFiltersHandler,
  fetchZonalGrossMarginListHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { zonalGrossMarginHeaderElements } from "./schema/header-elements";
import { ZonalGrossMarginListResponse } from "./schema/zonal-gross-margin.schema";
import { zonalGrossMarginFilterSchema } from "./schema/zonal-gross-margin-filter.schema";

export const ZonalGrossMarginPage = () => {
  const navigate = useNavigate();
  const { hasWriteAccess } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingZonalGrossMargin, setLoadingZonalGrossMargin] = useState(false);
  const [zonalGrossMarginList, setZonalGrossMarginList] =
    useState<ZonalGrossMarginListResponse | null>(null);
  const [page, setPage] = useState(1);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<
    Partial<GetZonalGrossMarginListProps> | undefined
  >();

  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver: zodResolver(zonalGrossMarginFilterSchema),
    defaultValues: {
      GranoProductivo: [],
      ZonaProductiva: [],
      CampanaProductiva: [],
      Equilibrio: "",
      RindeEstimado: "",
    },
  });

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchZonalGrossMarginList = async (
    filters?: Partial<GetZonalGrossMarginListProps>,
    showLoad = true,
  ) => {
    await fetchZonalGrossMarginListHandler({
      setLoading: setLoadingZonalGrossMargin,
      setZonalGrossMarginList: setZonalGrossMarginList,
      showLoad,
      filters,
      navigate,
      fetchPermissions: async () => {},
    });
  };

  const submitFilters = async () => {
    await submitFiltersHandler({
      event: getValues(),
      setOpenFilters,
      setFiltersApplied,
      setCurrentFilters,
      setPage,
      fetchZonalGrossMarginList,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      getValues,
      setOpenFilters,
      submitFilters,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchZonalGrossMarginList();
    }
  }, [isActive]);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchZonalGrossMarginList({ ...currentFilters, "P.Pagina": nextPage }, true);
  };

  const handleAddZonalGrossMargin = () => {
    navigate(AppRoutes.ZONAL_GROSS_MARGINS_CREATION);
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.ZONAL_GROSS_MARGINS_DETAIL.replace(":id", String(id)));
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("es-AR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const items = (zonalGrossMarginList?.listado ?? []).map((item) => ({
    ...item,
    equilibrio: formatNumber(item.equilibrio),
    rindeEstimado: formatNumber(item.rindeEstimado),
  }));

  const values = useWatch({ control });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      data-testid="zonal-gross-margin-page"
    >
      <SectionHeader
        title="Margen Bruto Zonal"
        addButtonLabel="Agregar"
        handleAdd={handleAddZonalGrossMargin}
        loading={loadingZonalGrossMargin}
        showButtons={hasWriteAccess(PermissionCode.ZONAL_GROSS_MARGIN)}
      />

      <ZonalGrossMarginFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        disableFilters={loadingZonalGrossMargin}
        loadingFilters={loadingZonalGrossMargin}
        filtersApplied={filtersApplied}
        disableClear={loadingZonalGrossMargin}
        disableApply={loadingZonalGrossMargin || filtersEmpty}
      />

      <Flex flexDir="column">
        {Boolean(!loadingZonalGrossMargin && !zonalGrossMarginList?.listado?.length) && (
          <NoData />
        )}

        {Boolean(!loadingZonalGrossMargin && zonalGrossMarginList?.listado?.length) && (
          <>
            <StandardTable
              selection={[]}
              setSelection={() => {}}
              indeterminate={false}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Editar"
              items={items}
              headers={zonalGrossMarginHeaderElements}
              actionElements={(item) => (
                <ZonalGrossMarginActions handleDetail={handleDetail} item={item} />
              )}
              noActions={!hasWriteAccess(PermissionCode.ZONAL_GROSS_MARGIN)}
              noSelection
            />

            {Boolean(zonalGrossMarginList?.totalPages && zonalGrossMarginList.totalPages > 1) && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={zonalGrossMarginList?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};